import "./QuestionProgressIndicator.scss";
import "firebase/firestore";

import firebase from "firebase/app";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { CSSTransition } from "react-transition-group";

import { ReactComponent as Check15 } from "../assets/check/15px.svg";
import { ReactComponent as Check19 } from "../assets/check/19px.svg";
import { ReactComponent as ArrowLine } from "../assets/question line/arrow.svg";
import { ReactComponent as Retry31Narrow } from "../assets/retry/31px-narrow.svg";
import { CardState } from "../db";
import {
  getIntervalLabelsForSchedule,
  getLevelForInterval,
  ReviewMarking,
  SpacedRepetitionSchedule
} from "../spacedRepetition";
import updateCardStateForReviewMarking from "../util/updateCardStateForReviewMarking";
import { CardType } from "../CardData";

const isEqual = require("lodash.isequal");

export type ReviewMarkingInteractionState = {
  reviewMarking: ReviewMarking;
  status: "pending" | "committed";
};

interface QuestionProgressIndicatorProps {
  cardState: CardState | null;
  cardType: CardType;
  reviewMarkingInteractionState: ReviewMarkingInteractionState | null;
  schedule: SpacedRepetitionSchedule;
}

export interface QuestionProgressIndicatorHandle {
  getLevelItemElements(): HTMLElement[];
}

const QuestionProgressIndicator = React.memo(function QuestionProgressIndicator(
  props: QuestionProgressIndicatorProps & {
    exportedRef: React.Ref<QuestionProgressIndicatorHandle>;
  },
) {
  const {
    reviewMarkingInteractionState,
    schedule,
    cardState,
    cardType,
    exportedRef,
  } = props;

  // TODO: extract, probably
  const defaultCardState = {
    interval: 0,
    bestInterval: null,
    needsRetry: false,
  };
  const { interval, bestInterval, needsRetry } = cardState || defaultCardState;

  const intervalLabels = getIntervalLabelsForSchedule(schedule);

  const currentLevel = getLevelForInterval(interval, schedule);
  const bestLevel =
    bestInterval === null ? null : getLevelForInterval(bestInterval, schedule);

  const marking =
    (reviewMarkingInteractionState &&
      reviewMarkingInteractionState.reviewMarking) ||
    null;

  const nextCardState = reviewMarkingInteractionState
    ? updateCardStateForReviewMarking(
        {
          baseCardState: cardState,
          cardType: cardType,
          reviewMarking: reviewMarkingInteractionState.reviewMarking,
          schedule: schedule,
          reviewTimestampMillis: Date.now(),
          fromMillis: firebase.firestore.Timestamp.fromMillis,
          generatedOrderSeed: 0
        },
      )
    : null;
  const nextCardLevel = nextCardState
    ? getLevelForInterval(nextCardState.interval, schedule)
    : null;

  const activeCardState = nextCardState || cardState;

  let progressLineLevel: number;
  if (
    (marking === "forgotten" &&
      ((nextCardLevel !== null && nextCardLevel < currentLevel) ||
        currentLevel > (bestLevel || 0))) ||
    (needsRetry && bestInterval !== null && bestInterval < interval)
  ) {
    progressLineLevel = Math.max(0, currentLevel - 1);
  } else if (marking === "remembered") {
    progressLineLevel = currentLevel;
  } else if (bestInterval !== null && bestInterval < interval) {
    progressLineLevel = Math.max(0, currentLevel - 0.5);
  } else {
    progressLineLevel = currentLevel;
  }

  function getProgressLineWidthForLevel(level: number) {
    return Math.max(0, 278 * (level / (intervalLabels.length - 1)) - 3);
  }
  const progressLineWidth = getProgressLineWidthForLevel(progressLineLevel);
  const activeBestLevel =
    activeCardState && activeCardState.bestInterval !== null
      ? getLevelForInterval(activeCardState.bestInterval, schedule)
      : null;
  const lostProgressLineWidth = getProgressLineWidthForLevel(
    activeBestLevel || 0,
  );

  const levelItemElementRefs = useRef<HTMLElement[]>([]);
  useImperativeHandle(exportedRef, () => ({
    getLevelItemElements: () => {
      return levelItemElementRefs.current;
    },
  }));

  return (
    <div className="QuestionProgressIndicator">
      <div className="ArrowLineContainer">
        <ArrowLine />
        <div
          className={`ProgressLine LostProgress`}
          style={{
            width: lostProgressLineWidth,
          }}
        ></div>
        <div
          className={`ProgressLine ${
            marking === "remembered" ? "JustGrew" : ""
          } ${
            reviewMarkingInteractionState &&
            reviewMarkingInteractionState.status === "pending"
              ? "Pending"
              : ""
          }`}
          style={{
            width: progressLineWidth,
          }}
        ></div>
      </div>
      <div className="ItemContainer">
        {intervalLabels.map((label, iteratedLevel) => (
          <QuestionProgressIndicatorItem
            ref={element => {
              if (element)
                levelItemElementRefs.current[iteratedLevel] = element;
              else delete levelItemElementRefs.current[iteratedLevel];
            }}
            key={iteratedLevel}
            label={label}
            when={
              iteratedLevel === currentLevel
                ? "current"
                : iteratedLevel > currentLevel
                ? "harder"
                : "easier"
            }
            isSelected={
              nextCardLevel === null
                ? iteratedLevel === currentLevel
                : nextCardLevel === iteratedLevel
            }
            isCompleted={iteratedLevel <= (bestLevel === null ? -1 : bestLevel)}
            needsRetry={(needsRetry || false) && iteratedLevel === currentLevel}
            pendingRetry={
              activeCardState !== null &&
              (activeCardState.needsRetry || false) &&
              iteratedLevel ===
                getLevelForInterval(activeCardState.interval, schedule)
            }
            reviewMarkingInteractionState={reviewMarkingInteractionState}
          />
        ))}
      </div>
    </div>
  );
});

interface QuestionProgressIndicatorItemProps {
  label: string;
  when: "easier" | "current" | "harder";
  isCompleted: boolean;
  needsRetry: boolean;
  pendingRetry: boolean;
  isSelected: boolean;
  reviewMarkingInteractionState: ReviewMarkingInteractionState | null;
}

const QuestionProgressIndicatorItemImpl = React.memo(
  function QuestionProgressIndicatorItem(
    props: QuestionProgressIndicatorItemProps & {
      exportedRef: React.Ref<HTMLDivElement>;
    },
  ) {
    const {
      label,
      when,
      isCompleted,
      needsRetry,
      pendingRetry,
      isSelected,
      reviewMarkingInteractionState,
    } = props;

    const reviewMarking =
      (reviewMarkingInteractionState &&
        reviewMarkingInteractionState.reviewMarking) ||
      null;
    const isCommitted =
      (reviewMarkingInteractionState &&
        reviewMarkingInteractionState.status === "committed") ||
      false;

    const displaysRetryIndicator =
      pendingRetry ||
      (needsRetry && reviewMarking === "remembered" && !isCommitted);

    const displaysBigMarkingIndicator =
      when === "current" &&
      reviewMarking === "remembered" &&
      isCommitted &&
      (!needsRetry || (needsRetry && (!isSelected || isCompleted)));

    return (
      <div
        ref={props.exportedRef}
        className={`Item ${when} ${isCompleted ? "complete" : "unattempted"} ${
          displaysRetryIndicator ? "needsRetry" : ""
        } ${isSelected ? "selected" : ""}  ${
          reviewMarkingInteractionState
            ? `${reviewMarkingInteractionState.reviewMarking} ${reviewMarkingInteractionState.status}`
            : ""
        }`}
      >
        <span className="Label">{label}</span>
        <div className="Marker">
          <CSSTransition
            in={
              isSelected &&
              !displaysBigMarkingIndicator &&
              !displaysRetryIndicator
            }
            timeout={1000}
            classNames="SelectionIndicator"
            unmountOnExit
          >
            <div className="SelectionIndicator" />
          </CSSTransition>
          <CSSTransition
            in={isCompleted}
            timeout={500}
            classNames="MarkingIndicator"
            unmountOnExit
          >
            <div
              className={`MarkingIndicator Complete ${
                displaysRetryIndicator ? "WithNeedsRetry" : ""
              }`}
            >
              <Check15 />
            </div>
          </CSSTransition>
          <CSSTransition
            in={
              !displaysRetryIndicator &&
              (when === "current" &&
                reviewMarking === "remembered" &&
                !isCommitted)
            }
            timeout={500}
            classNames="MarkingIndicator"
            unmountOnExit
          >
            <div className={`MarkingIndicator Complete Pending`}>
              <Check15 />
            </div>
          </CSSTransition>
          <CSSTransition
            in={displaysRetryIndicator}
            timeout={500}
            classNames="MarkingIndicator"
            unmountOnExit
          >
            <div
              className={`MarkingIndicator NeedsRetry ${
                isCommitted
                  ? "Committed"
                  : reviewMarkingInteractionState &&
                    reviewMarkingInteractionState.status === "pending"
                  ? "Pending"
                  : ""
              }`}
            >
              <Retry31Narrow />
            </div>
          </CSSTransition>
          <CSSTransition
            in={displaysBigMarkingIndicator}
            timeout={1000}
            classNames="MarkingIndicator"
            unmountOnExit
          >
            <div className="MarkingIndicator Committed Complete">
              <Check19 />
            </div>
          </CSSTransition>
        </div>
      </div>
    );
  },
  isEqual,
);

const QuestionProgressIndicatorItem = forwardRef(
  (
    props: QuestionProgressIndicatorItemProps,
    ref: React.Ref<HTMLDivElement>,
  ) => <QuestionProgressIndicatorItemImpl {...props} exportedRef={ref} />,
);

export default forwardRef(
  (
    props: QuestionProgressIndicatorProps,
    ref: React.Ref<QuestionProgressIndicatorHandle>,
  ) => <QuestionProgressIndicator {...props} exportedRef={ref} />,
);
